/**
 * Doctor Write Prescription for patient.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { appointmentAPI, prescriptionAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';

const WritePrescription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patient') || '';
  const appointmentId = searchParams.get('appointment') || '';
  const prescriptionId = searchParams.get('prescription') || '';
  const isEditing = Boolean(prescriptionId);

  const [loading, setLoading] = useState(false);
  const [resolvingPatient, setResolvingPatient] = useState(false);
  const [loadingPrescription, setLoadingPrescription] = useState(isEditing);
  const [form, setForm] = useState({
    patient_uid: patientId,
    appointment_id: appointmentId,
    diagnosis: '',
    instructions: '',
    medications: [{ name: '', dosage: '', frequency: '', duration: '' }],
    notes: '',
    follow_up_date: '',
  });

  useEffect(() => {
    const loadPrescription = async () => {
      if (!prescriptionId) {
        setLoadingPrescription(false);
        return;
      }

      setLoadingPrescription(true);
      try {
        const res = await prescriptionAPI.getDetail(prescriptionId);
        const data = res.data || {};
        setForm({
          patient_uid: data.patient_uid || patientId,
          appointment_id: data.appointment_id || appointmentId,
          diagnosis: data.diagnosis || '',
          instructions: data.instructions || '',
          medications: Array.isArray(data.medications) && data.medications.length > 0
            ? data.medications
            : [{ name: '', dosage: '', frequency: '', duration: '' }],
          notes: data.notes || '',
          follow_up_date: data.follow_up_date || '',
        });
      } catch (err) {
        toast.error(err?.message || 'Unable to load prescription details.');
        navigate('/doctor/prescriptions', { replace: true });
      } finally {
        setLoadingPrescription(false);
      }
    };

    loadPrescription();
  }, [appointmentId, navigate, patientId, prescriptionId]);

  useEffect(() => {
    const resolvePatientFromAppointment = async () => {
      if (!appointmentId || form.patient_uid) return;

      setResolvingPatient(true);
      try {
        const res = await appointmentAPI.getDetail(appointmentId);
        const patientUid = res.data?.patient_uid || '';
        if (!patientUid) {
          toast.error('Patient not found for this appointment.');
          return;
        }
        setForm(f => ({ ...f, patient_uid: patientUid }));
      } catch (err) {
        toast.error(err?.message || 'Unable to load appointment details.');
      } finally {
        setResolvingPatient(false);
      }
    };

    resolvePatientFromAppointment();
  }, [appointmentId, form.patient_uid]);

  const addMedication = () => {
    setForm(f => ({
      ...f,
      medications: [...f.medications, { name: '', dosage: '', frequency: '', duration: '' }]
    }));
  };

  const removeMedication = (idx) => {
    setForm(f => ({
      ...f,
      medications: f.medications.filter((_, i) => i !== idx)
    }));
  };

  const updateMedication = (idx, field, value) => {
    setForm(f => ({
      ...f,
      medications: f.medications.map((m, i) => i === idx ? { ...m, [field]: value } : m)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.patient_uid) {
      toast.error('No patient selected. Please navigate here from a patient\'s appointment.');
      return;
    }
    if (!form.diagnosis) {
      toast.error('Please enter a diagnosis.');
      return;
    }
    if (form.medications.some(m => !m.name)) {
      toast.error('Please fill in all medication names.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        instructions: form.instructions || form.notes || form.diagnosis,
      };

      if (isEditing) {
        await prescriptionAPI.update(prescriptionId, payload);
        toast.success('Prescription updated successfully.');
      } else {
        await prescriptionAPI.create(payload);
        toast.success('Prescription created and sent to patient.');
      }

      navigate('/doctor/prescriptions');
    } catch (err) {
      toast.error(
        typeof err === 'string'
          ? err
          : (err?.error || err?.message || (isEditing ? 'Failed to update prescription.' : 'Failed to create prescription.'))
      );
    } finally {
      setLoading(false);
    }
  };

  if (loadingPrescription) {
    return (
      <Layout title={isEditing ? 'Edit Prescription' : 'Write Prescription'} role="doctor" showBack>
        <div className="p-4 pb-24 space-y-4">
          <div className="skeleton h-28 rounded-2xl"></div>
          <div className="skeleton h-64 rounded-2xl"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={isEditing ? 'Edit Prescription' : 'Write Prescription'} role="doctor" showBack>
      <div className="p-4 pb-24">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="card space-y-4">
            <h3 className="font-semibold text-gray-900">Diagnosis</h3>
            <textarea
              value={form.diagnosis}
              onChange={e => setForm(f => ({ ...f, diagnosis: e.target.value }))}
              className="input-field"
              rows="3"
              placeholder="Enter diagnosis details..."
              required
            />
          </div>

          <div className="card space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Medications</h3>
              <button type="button" onClick={addMedication} className="text-accent-500 text-sm font-medium">
                + Add Medication
              </button>
            </div>

            {form.medications.map((med, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500">Medication #{idx + 1}</span>
                  {form.medications.length > 1 && (
                    <button type="button" onClick={() => removeMedication(idx)} className="text-red-500 text-xs">Remove</button>
                  )}
                </div>
                <input
                  type="text"
                  value={med.name}
                  onChange={e => updateMedication(idx, 'name', e.target.value)}
                  className="input-field"
                  placeholder="Medication name"
                  required
                />
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={med.dosage}
                    onChange={e => updateMedication(idx, 'dosage', e.target.value)}
                    className="input-field text-sm"
                    placeholder="Dosage"
                  />
                  <input
                    type="text"
                    value={med.frequency}
                    onChange={e => updateMedication(idx, 'frequency', e.target.value)}
                    className="input-field text-sm"
                    placeholder="Frequency"
                  />
                  <input
                    type="text"
                    value={med.duration}
                    onChange={e => updateMedication(idx, 'duration', e.target.value)}
                    className="input-field text-sm"
                    placeholder="Duration"
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="card space-y-4">
            <h3 className="font-semibold text-gray-900">Instructions & Notes</h3>
            <div>
              <label className="label">Patient Instructions</label>
              <textarea
                value={form.instructions}
                onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))}
                className="input-field"
                rows="2"
                placeholder="How to take medications, lifestyle advice..."
              />
            </div>
            <div>
              <label className="label">Doctor Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="input-field"
              rows="2"
              placeholder="Internal clinical notes..."
            />
            </div>

            <div>
              <label className="label">Follow-up Date</label>
              <input
                type="date"
                value={form.follow_up_date}
                onChange={e => setForm(f => ({ ...f, follow_up_date: e.target.value }))}
                className="input-field"
              />
            </div>
          </div>

          {appointmentId && !form.patient_uid && (
            <div className="card bg-yellow-50 border-yellow-200 text-sm text-yellow-800">
              {resolvingPatient ? 'Loading patient details from the appointment...' : 'Patient details could not be resolved from this appointment.'}
            </div>
          )}

          <button type="submit" disabled={loading || resolvingPatient || loadingPrescription || !form.patient_uid} className="w-full py-3 rounded-xl font-semibold text-white bg-accent-500 hover:bg-accent-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
            {loading && <div className="spinner w-5 h-5 border-2"></div>}
            {isEditing ? 'Update Prescription' : 'Submit Prescription'}
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default WritePrescription;
